"use client";

import { useTranslations } from "next-intl";
import React, { useEffect, useState } from "react";
import type { TransactionDetail } from "@/types/glassroom";
import {
  Paper,
  Text,
  DetailRow,
  TbQrcode,
  TbCalendar,
  TbCoins,
  TbTarget,
  TbNote,
  TbUser,
  TbArrowRight,
  TbTransfer,
  TbArchive,
} from "@forestsync/ui";
import { TRANSACTION_TYPES, USER_ROLES } from "@forestsync/db";
import { QRCode } from "@/components/glassroom/common";
import { HyperLink } from "@/components/glassroom/common/HyperLink";
import { TransactionForests } from "./TransactionForests";
import {
  getTransactionTypeBgColor,
  getTransactionTypeName,
} from "@forestsync/lib/type-utils";

export function TransactionView({
  transaction,
}: {
  transaction: TransactionDetail;
}) {
  const t = useTranslations();
  const [currentUrl, setCurrentUrl] = useState<string>("");

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  const getTransactionTypeIcon = (type: number) => {
    switch (type) {
      case TRANSACTION_TYPES.ISSUANCE:
        return TbArrowRight;
      case TRANSACTION_TYPES.TRADE:
        return TbTransfer;
      case TRANSACTION_TYPES.RETIREMENT:
        return TbArchive;
      default:
        return TbNote;
    }
  };

  const totalPrice = transaction.pricePerTon
    ? transaction.pricePerTon * transaction.totalTokens
    : null;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-8">
      {/* Main Transaction Info */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left: Transaction ID and QR Code */}
        <div className="flex flex-col gap-4 md:col-span-5">
          <Paper
            withBorder
            radius="lg"
            p="md"
            className={`flex flex-col shadow-md h-full min-h-50 ${getTransactionTypeBgColor(transaction.transactionType)}`}
          >
            <div className="mb-4">
              <Text size="lg" fw={700} className="mb-2">
                {t("transaction.title")}
              </Text>
              <Text
                size="xl"
                className="font-mono text-gray-700 dark:text-gray-300 break-all"
              >
                {transaction.refId}
              </Text>
            </div>
            <div className="flex-1 flex items-center justify-center">
              {currentUrl ? (
                <QRCode
                  key={currentUrl}
                  value={currentUrl}
                  size={200}
                  className="mx-auto"
                />
              ) : (
                <TbQrcode
                  size={64}
                  className="text-blue-400 dark:text-blue-300"
                />
              )}
            </div>
          </Paper>
        </div>

        {/* Right: Transaction Details */}
        <div className="flex flex-col gap-4 md:col-span-7">
          {/* Transaction Summary Card */}
          <Paper withBorder radius="lg" p="md" className="shadow-md">
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {/* Transaction Type */}
              <DetailRow
                icon={getTransactionTypeIcon(transaction.transactionType)}
                label={t("transaction.type")}
                value={getTransactionTypeName(transaction.transactionType, t)}
                className="py-2"
              />

              {/* Date */}
              <DetailRow
                icon={TbCalendar}
                label={t("transaction.date")}
                value={new Date(transaction.timestamp).toLocaleDateString()}
                className="py-2"
              />

              {/* Amount of Credits */}
              <DetailRow
                icon={TbTarget}
                label={t("transaction.amountOfCredits")}
                value={transaction.totalTokens.toString()}
                className="py-2"
              />

              {/* Price Information */}
              {transaction.pricePerTon && (
                <>
                  <DetailRow
                    icon={TbCoins}
                    label={t("transaction.pricePerTon")}
                    value={`€${transaction.pricePerTon.toFixed(2)}`}
                    className="py-2"
                  />
                  {totalPrice && (
                    <DetailRow
                      icon={TbCoins}
                      label={t("transaction.totalPrice")}
                      value={`€${totalPrice.toFixed(2)}`}
                      className="py-2"
                    />
                  )}
                </>
              )}

              {/* From User */}
              {transaction.fromUserName && (
                <DetailRow
                  icon={TbUser}
                  label={t("transaction.from")}
                  value={
                    transaction.fromUserRefId ? (
                      <HyperLink
                        type={
                          transaction.fromUserRole === USER_ROLES.BENEFICIARY
                            ? "b"
                            : "pd"
                        }
                        refId={transaction.fromUserRefId}
                      >
                        {transaction.fromUserName}
                      </HyperLink>
                    ) : (
                      transaction.fromUserName
                    )
                  }
                  className="py-2"
                />
              )}

              {/* To User */}
              {transaction.toUserName && (
                <DetailRow
                  icon={TbUser}
                  label={t("transaction.to")}
                  value={
                    transaction.toUserRefId ? (
                      <HyperLink
                        type={
                          transaction.transactionType ===
                          TRANSACTION_TYPES.ISSUANCE
                            ? "pd"
                            : "b"
                        }
                        refId={transaction.toUserRefId}
                      >
                        {transaction.toUserName}
                      </HyperLink>
                    ) : (
                      transaction.toUserName
                    )
                  }
                  className="py-2"
                />
              )}

              {/* Description */}
              {transaction.description && (
                <DetailRow
                  icon={TbNote}
                  label={t("transaction.description")}
                  value={transaction.description}
                  className="py-2"
                  layout="paragraph"
                />
              )}
            </div>
          </Paper>
        </div>
      </div>

      {/* Forests and Tokens Section */}
      <TransactionForests transaction={transaction} />
    </div>
  );
}
